import React, { useState, useEffect } from "react";
import styles from "./css/UsersPage.module.css";
import UserManagementTable from "@/components/tables/UserManagementTable";
import { SearchBar } from "@/components/bars/SearchBar";
import { Button } from "@/components/Buttons";
import { UserData } from "@/types";
import { useSelector, useDispatch } from "react-redux";
import { useGetAllUsersSuperAdmin } from "@/hooks/user/useGetAllUsersSuperAdmin";
import { RootState } from "@/store/store";
import { setToast } from "@/store/systemSlice";
import { useTranslation } from 'react-i18next';
import AddUserModal from "@/components/modals/AddUserModal";

export const SuperAdminUsersPage: React.FC = () => {
  const { t } = useTranslation('SuperAdminUsers');
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);

  const getAllUsersSuperAdminMutation = useGetAllUsersSuperAdmin({
    onSuccess: (data) => {
      setUsers(data);
      setFilteredUsers(data);
      setLoading(false);
    },
    onError: (error: any) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה בטעינת המשתמשים",
          toastType: "error",
        })
      );
      setLoading(false);
    },
  });

  useEffect(() => {
    // Check if user is super admin
    if (user.user_type !== "super_admin") {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: "אין לך הרשאות לצפות בעמוד זה",
          toastType: "error",
        })
      );
      return;
    }

    fetchUsers();
  }, [user.user_type]);

  const fetchUsers = () => {
    setLoading(true);
    getAllUsersSuperAdminMutation.mutate({});
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value === "") {
      setFilteredUsers(users);
    } else {
      const searchLower = value.toLowerCase();
      setFilteredUsers(
        users.filter(
          (user) =>
            user.email.toLowerCase().includes(searchLower) ||
            user.user_name.toLowerCase().includes(searchLower) ||
            (user.city_name && user.city_name.toLowerCase().includes(searchLower))
        )
      );
    }
  };

  const handleDataChange = () => {
    // Refresh the users list after status change or password reset
    fetchUsers();
  };

  if (user.user_type !== "super_admin") {
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>{t("accessDenied")}</div>
          </div>
          <div className={styles.tabView}>
            <p>{t("accessDeniedMessage")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.title}>{t("title")}</div>
          <div className={styles.subtitle}>{t("subtitle")}</div>
        </div>
        <div className={styles.tabView}>
          <div className={styles.managment}>
            <div className={styles.searchRow}>
              <div className={styles.labelInput}>
                <div className={styles.text}>{t("searchPlaceholder")}</div>
                <SearchBar
                  options={users.map((user) => ({
                    id: user.id,
                    name: user.email,
                  }))}
                  onOptionClick={() => {}}
                  value={searchText}
                  onChange={handleSearchChange}
                  isListNeeded={false}
                />
              </div>
              <Button
                text={t("addUserButton")}
                icon="no"
                onClick={() => setShowAddUserModal(true)}
                className={styles.addButton}
                id="add-user-btn"
              />
            </div>
            
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>{t("loadingUsers")}</p>
              </div>
            ) : (
              <UserManagementTable 
                data={filteredUsers} 
                onDataChange={handleDataChange}
                onRowClick={(user) => setEditingUser(user)}
              />
            )}
            
            {!loading && filteredUsers.length === 0 && (
              <div className={styles.noData}>
                <p>{searchText ? t("noSearchResults") : t("noUsersFound")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add/Edit User Modal */}
      <AddUserModal
        isOpen={showAddUserModal || editingUser !== null}
        editUser={editingUser}
        onClose={() => {
          setShowAddUserModal(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          setShowAddUserModal(false);
          setEditingUser(null);
          fetchUsers();
        }}
      />
    </div>
  );
};